import { authentication, logger } from '@ecosystem/newron-client-lib';
import axios from 'axios';
import { deepEqual } from '../tools/object';
import { Preference, ValueSet } from '../types';
import { SharePermissionsBody, UserAccess } from '../types/content/custom.types';
import { isSharedPreference } from '../common/util';
import { PreferenceValidator } from './validation/preference.validator';
import { PreferenceValidationErrors } from './validation/preference.validation.errors';

const PREFERENCE_API_URL = '/preference-filters/v1/preferences';
const PRODUCT = 'newron';

export class PreferenceClient {
  private static _instance: PreferenceClient;
  private preferenceCache: Record<string, Preference> = {};
  private uniquePreferenceCache: Record<string, Preference[]> = {};
  private readonly validator: PreferenceValidator;

  private constructor() {
    this.validator = PreferenceValidator.getInstance();
  }

  static getInstance(): PreferenceClient {
    if (!PreferenceClient._instance) {
      PreferenceClient._instance = new PreferenceClient();
    }
    return PreferenceClient._instance;
  }

  async retrieve(name: string): Promise<ValueSet | undefined> {
    try {
      // return from cache if available
      if (this.preferenceCache[name]) {
        return this.preferenceCache[name].data;
      }
      const token = await authentication.getAccessToken();
      const { data: savedPreferences } = await axios.get<Preference[]>(`${PREFERENCE_API_URL}/${PRODUCT}/${name}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (savedPreferences.length === 0) {
        return undefined;
      } else {
        const savedPreference = savedPreferences?.[0];
        this.preferenceCache[name] = { ...savedPreference, data: structuredClone(savedPreference.data) };
        return savedPreference.data;
      }
    } catch (err) {
      logger.error(new Error(err), {
        info: 'Failed to retrieve preference data from the server. Using default preferences',
      });
    }
  }

  async store(name: string, preference: ValueSet): Promise<ValueSet> {
    const oldPreference = this.preferenceCache[name];
    if (oldPreference && deepEqual(oldPreference.data, preference)) {
      return;
    }

    if (Object.keys(preference).length === 0) {
      await this.delete(name, oldPreference.id);
      this.preferenceCache[name] = undefined;
      return;
    }

    let sanitizedPreference: ValueSet;
    try {
      sanitizedPreference = this.validator.validate(name, preference);
    } catch (error) {
      if (error instanceof PreferenceValidationErrors) {
        console.error(error.toString());
        logger.error(error, {
          info: error.toString(),
        });
      }
      throw error;
    }

    try {
      const token = await authentication.getAccessToken();
      const savedPreference = !oldPreference
        ? await this.createNew(name, sanitizedPreference, token)
        : await this.updateExisting(name, oldPreference.id, sanitizedPreference, token);
      this.preferenceCache[name] = { ...savedPreference, data: structuredClone(savedPreference.data) };

      return savedPreference.data;
    } catch (err) {
      logger.error(new Error(err), {
        info: 'Failed to store preference data on the server. Using default preferences',
      });
    }
  }

  async createNew(name: string, preference: ValueSet, token: string): Promise<Preference> {
    const { data } = await axios.post<Preference>(
      `${PREFERENCE_API_URL}/${PRODUCT}/${name}`,
      { data: preference },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  }

  async updateExisting(
    name: string,
    oldPreferenceId: string,
    preference: ValueSet,
    token: string
  ): Promise<Preference> {
    const { data } = await axios.patch<Preference>(
      `${PREFERENCE_API_URL}/${PRODUCT}/${name}/${oldPreferenceId}`,
      { data: preference },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  }

  async delete(name: string, id: string): Promise<void> {
    try {
      const token = await authentication.getAccessToken();
      await axios.delete(`${PREFERENCE_API_URL}/${PRODUCT}/${name}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      this.uniquePreferenceCache[name] = this.uniquePreferenceCache[name]?.filter((pref: Preference) => pref.id !== id);
    } catch (err) {
      logger.error(new Error(err), {
        info: 'Failed to delete preference data on the server. Using default preferences',
      });
    }
  }

  /**
   * Creates a new unique preference within the given name, with allowMultiple set to true
   * @param name the name
   * @param preference the preference to store
   * @returns the stored preference
   */
  async createNewUnique(name: string, preference: ValueSet): Promise<Preference> {
    let sanitizedPreference: ValueSet;
    try {
      sanitizedPreference = this.validator.validate(name, preference);
    } catch (error) {
      if (error instanceof PreferenceValidationErrors) {
        console.error(error.toString());
        logger.error(error, {
          info: error.toString(),
        });
      }
      throw error;
    }

    try {
      const token = await authentication.getAccessToken();
      const { data } = await axios.post<Preference>(
        `${PREFERENCE_API_URL}/${PRODUCT}/${name}?allowMultiple=true`,
        { data: sanitizedPreference },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      this.uniquePreferenceCache[name] = [...(this.uniquePreferenceCache[name] || []), data];

      return data;
    } catch (err) {
      logger.error(new Error(err), {
        info: 'Failed to store preference data on the server.',
      });
    }
  }

  /**
   * Gets all the unique preferences within the given name
   * @param name the name
   * @returns list of stored preferences
   */
  async getAllUnique(name: string, isShared: boolean = false): Promise<Preference[] | undefined> {
    const sharedParam = isSharedPreference(isShared);
    if (this.uniquePreferenceCache[name]) {
      return this.uniquePreferenceCache[name];
    }

    try {
      const token = await authentication.getAccessToken();
      const { data: savedPreferences } = await axios.get<Preference[]>(
        `${PREFERENCE_API_URL}/${PRODUCT}/${name}${sharedParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      this.uniquePreferenceCache[name] = savedPreferences;

      return savedPreferences;
    } catch (err) {
      logger.error(new Error(err), {
        info: 'Failed to retrieve preference data from the server.',
      });
    }
  }

  /**
   * Function to get permissions for a preference
   * @param name preference type name
   * @param preferenceId preference id
   * @returns list of users with their access level
   */
  async getPermissions(name: string, preferenceId: string, isShared: boolean = false): Promise<UserAccess[]> {
    const token = await authentication.getAccessToken();
    const sharedParam = isSharedPreference(isShared);
    const { data } = await axios.get<UserAccess[]>(
      `${PREFERENCE_API_URL}/${PRODUCT}/${name}/${preferenceId}/permissions${sharedParam}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  }

  /**
   * Function to add permissions to a preference
   * @param name preference type name
   * @param preferenceId preference id
   * @param permissions list of users with their access level to be sent as body, owner must be included
   * @returns returns OK if successful
   */
  async addPermissions(name: string, preferenceId: string, permissions: SharePermissionsBody[]): Promise<string> {
    const token = await authentication.getAccessToken();

    const { data } = await axios.patch<string>(
      `${PREFERENCE_API_URL}/${PRODUCT}/${name}/${preferenceId}/permissions`,
      permissions,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  }

  /**
   * Function to remove user from a preference
   * @param name preference type name
   * @param preferenceId preference id
   * @param userId user id to be removed
   */
  async deletePermissions(name: string, preferenceId: string, userId: string): Promise<void> {
    try {
      const token = await authentication.getAccessToken();
      await axios.delete(`${PREFERENCE_API_URL}/${PRODUCT}/${name}/${preferenceId}/permissions/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      logger.error(new Error(err), {
        info: 'Failed to unshare.',
      });
    }
  }
}
